import React from 'react';
import { Button, Divider, Input } from 'antd';
import KiwiBanner from 'components/KiwiBanner';
import { useLocation, useNavigate } from 'react-router-dom';
import GoogleLogin from './components/GoogleLogin';
import { PATH } from 'constants/path';
import { ERROR_CODE } from 'constants/common';
import s from './s.module.less';
import WarningIcon from 'assets/common/warningRed.svg';
import { isValidEmail } from 'utils/form';
import Check from 'assets/common/check.svg';
import ForgotPassword from './components/ForgotPassword';
import { loginWithPwd, ERegisterType } from 'api/public';
import { getCurrentUser } from 'api/common';
import { getHomeInfo } from 'api/provider';
import { getStep } from 'api/ppaOnboarding';
import { checkLeadFormByEmail } from 'api/ppaLeadForm';
import { getIntentRedirect } from 'pages/PPALeadFormPage/getIntentRedirect';
import { INVITED_STEP_ROUTE, isInvitedProviderStep } from 'pages/InvitedProviderOnboarding/steps';
import { ERoleCode } from 'types/enumItems';
import { ProviderBasicInfo } from 'types/provider';
import ProviderStore from 'store/Provider';
import {
    increaseLoginCount,
    setHasOnceSuccessLogin,
    setScheduleToken,
    setUserId,
    setUserToken,
} from 'utils/localstore';
import { getResetFailEmail, setResetFailEmail, setCurrentPlatformType } from 'utils/sessionStorage';
import { clearUniprofileStepStorage } from 'utils/uniprofile';
import { isMobile } from 'utils/common';
import { isPPAOnboardingCompleted } from 'utils/common';

export default function ProviderLoginPage() {
    const navigate = useNavigate();
    const { state } = useLocation();
    const [psw, setPsw] = React.useState('');
    const [email, setEmail] = React.useState(state?.email || '');
    const [showPwsError, setShowPwsError] = React.useState(false);
    // Non-credential login failures the user can actually act on (currently
    // STAFF_NO_PROVIDER_SCOPE) must not render as "wrong password" — that sends
    // group-practice staff into an endless password-reset loop. Null = default copy.
    const [loginErrorMsg, setLoginErrorMsg] = React.useState<string | null>(null);
    const [showResetSuccess, setShowResetSuccess] = React.useState(!!state?.showResetSuccess);
    const [showForgotPassword, setShowForgotPassword] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const showEmailError = email && !isValidEmail(email);
    const [failEmail, setFailEmail] = React.useState('');
    const [, set] = ProviderStore.useStore();

    React.useEffect(() => {
        const resetFail = getResetFailEmail();
        if (resetFail) {
            setFailEmail(resetFail);
            setShowForgotPassword(true);
            setResetFailEmail();
        }
    }, []);
    // Fetch fresh user info after login (required regardless of group practice or not)
    // and route group practice users to the invited onboarding entry — unless they've
    // already completed onboarding, in which case go straight to the dashboard.
    const fetchUserAndNavigate = async (fallback: string) => {
        let userEmail: string | undefined;
        try {
            const userRes = await getCurrentUser();
            const userData = userRes?.data?.data;
            userEmail = userData?.actingUser?.email;
            const isGroupPractice = userData?.roleCode === ERoleCode.GROUP_PROVIDER;
            if (isGroupPractice) {
                const homeRes = await getHomeInfo();
                if (isPPAOnboardingCompleted(homeRes?.data?.data)) {
                    navigate(fallback);
                    return;
                }
                const stepRes = await getStep();
                const savedStep = stepRes?.data?.data?.currentStep;
                const target = isInvitedProviderStep(savedStep)
                    ? INVITED_STEP_ROUTE[savedStep]
                    : PATH.INVITED_PROVIDER_ONBOARDING_CREATE_PROFILE;
                navigate(target);
                return;
            }
        } catch (error) {
            console.error(error);
        }

        try {
            if (userEmail) {
                const leadRes = await checkLeadFormByEmail({ email: userEmail });
                const practiceHelpType = leadRes?.data?.data?.leadForm?.practiceHelpType;
                if (practiceHelpType) {
                    const target = await getIntentRedirect(practiceHelpType);
                    navigate(target);
                    return;
                }
            }
        } catch (error) {
            console.error(error);
        }

        navigate(fallback);
    };

    const loginWithPwdFun = async () => {
        setLoading(true);
        // Clear last attempt's copy: a stale scope message must not survive into a
        // subsequent genuine credential failure.
        setLoginErrorMsg(null);
        const result = await loginWithPwd({
            email,
            password: psw,
            type: ERegisterType.KIWI_PORTAL,
        });
        if (!result.error) {
            const info: ProviderBasicInfo = result.data.data.authPaylod;
            const { token } = result.data.data;
            setHasOnceSuccessLogin('true');
            set('data', {
                ...info,
            });
            setUserId(info.id);
            increaseLoginCount();
            clearUniprofileStepStorage();
            setCurrentPlatformType(ERegisterType.KIWI_PORTAL);
            setUserToken(token);
            setScheduleToken(token);
            if (isMobile()) {
                navigate(PATH.MOBILE_WELCOME);
            } else {
                await fetchUserAndNavigate(`${PATH.DASHBOARD}/${PATH.PROVIDER_HOME}`);
            }
        } else {
            // loginWithPwd 也传了 disableErrorNote，后端文案同样不会自动弹出。
            const isScopeError = (result as any)?.errorCode === ERROR_CODE.STAFF_NO_PROVIDER_SCOPE;
            setLoginErrorMsg(isScopeError ? (result as any)?.error || null : null);
            setShowPwsError(true);
        }
        setLoading(false);
    };

    const onGoogleLoginCallback = async (result: any) => {
        if (!result.error) {
            const info: ProviderBasicInfo = result.data.data.authPaylod;
            const { token } = result.data.data;
            setHasOnceSuccessLogin('true');
            set('data', {
                ...info,
            });
            setUserId(info.id);
            increaseLoginCount();
            clearUniprofileStepStorage();
            // if (isOps) {
            //     setOpsToken(token);
            // } else {
            //     setUserToken(token);
            // }
            setUserToken(token);
            setScheduleToken(token);
            setCurrentPlatformType(ERegisterType.KIWI_PORTAL);
            if (isMobile()) {
                navigate(PATH.MOBILE_WELCOME);
            } else {
                await fetchUserAndNavigate(`${PATH.DASHBOARD}/${PATH.PROVIDER_HOME}`);
            }
            // setLoginTimeToLocalStorage();
        }
    };
    return (
        <div className={`${s.wrap} flex`}>
            <div className={s.left}>
                <div className={s.container}>
                    <h2 className={s.headerTitle}>
                        <img src="https://saas-prod-us.s3.us-east-2.amazonaws.com/prod/ui/logo_kiwihealth_by_klarity_380x96.png" />
                    </h2>
                    <div className={s.subTitle}>Log in</div>
                    <div className={s.navTips}>
                        New to Kiwi Health?
                        <span
                            style={{ color: '#006957', cursor: 'pointer' }}
                            onClick={() => {
                                window.location.href = 'https://www.kiwihealth.com/klarity-provider-signup';
                            }}
                        >
                            {' '}
                            Sign up
                        </span>
                    </div>
                    <div className={s.form}>
                        {/* disable chrome auto fill */}
                        <input type="text" style={{ display: 'none' }} name="email" disabled autoComplete="off" />
                        <input type="password" style={{ display: 'none' }} disabled autoComplete="off" />
                        <div className={s.label}>Email address</div>
                        <div className={s.value}>
                            <Input
                                onChange={(e) => {
                                    setEmail(e.target.value);
                                    setShowResetSuccess(false);
                                }}
                                autoComplete="off"
                                value={email}
                                size="large"
                                status={showEmailError ? 'error' : undefined}
                            />
                        </div>
                        <div className={s.label}>Password</div>
                        <div className={s.value} style={{ marginBottom: '12px' }}>
                            <Input.Password
                                value={psw}
                                autoComplete="off"
                                onChange={(e) => {
                                    setPsw(e.target.value);
                                    setShowResetSuccess(false);
                                }}
                                size="large"
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        if (email && psw) {
                                            loginWithPwdFun();
                                        }
                                    }
                                }}
                            />
                        </div>
                        <div className={showPwsError ? `${s.existError} ${s.existErrorShow}` : s.existError}>
                            <img src={WarningIcon} />
                            <span>
                                {loginErrorMsg ||
                                    "The password you've entered doesn't match the email provided. Please double-check and try again."}
                            </span>
                        </div>

                        <div className={showResetSuccess ? `${s.success} ${s.successShow}` : s.success}>
                            <img src={Check} />
                            <span>Password reset successful</span>
                        </div>

                        <div
                            className={s.forgetWrap}
                            onClick={() => {
                                setShowForgotPassword(true);
                            }}
                        >
                            Forgot password?
                        </div>
                        <Button
                            onClick={loginWithPwdFun}
                            style={{ marginTop: 4 }}
                            block
                            loading={loading}
                            type="primary"
                            size="large"
                            disabled={!email || !psw}
                        >
                            Continue
                        </Button>
                    </div>

                    <Divider plain className={s.divi}>
                        Or
                    </Divider>

                    <div className={s.googleWrap}>
                        <GoogleLogin onCallback={onGoogleLoginCallback} />
                    </div>
                </div>
            </div>
            <div className={s.right}>
                <div className={s.banner}>
                    <KiwiBanner />
                </div>
            </div>
            <ForgotPassword
                defaultFailEmail={failEmail}
                show={showForgotPassword}
                onCancel={() => {
                    //clear email
                    setFailEmail('');
                    setShowForgotPassword(false);
                }}
            />
        </div>
    );
}
